import type { paths as ResearchApiPaths } from "../types/research-api.generated";
import * as api from "./client.js";

type ResearchCommentariesPath = ResearchApiPaths["/api/v1/commentaries"];
type ResearchCommentaryByIdPath = ResearchApiPaths["/api/v1/commentaries/{id}"];
type CommentariesQuery = NonNullable<ResearchCommentariesPath["get"]["parameters"]["query"]>;

export type CommentariesListResponse =
  ResearchCommentariesPath["get"]["responses"]["200"]["content"]["application/json"];
type CommentarySingleResponse =
  ResearchCommentariesPath["post"]["responses"]["201"]["content"]["application/json"];
type CommentaryUpdateSingleResponse =
  ResearchCommentaryByIdPath["put"]["responses"]["200"]["content"]["application/json"];
export type CreateCommentaryRequest =
  ResearchCommentariesPath["post"]["requestBody"]["content"]["application/json"];
export type UpdateCommentaryRequest =
  ResearchCommentaryByIdPath["put"]["requestBody"]["content"]["application/json"];

export type Commentary = CommentariesListResponse["commentaries"][number];

export type CommentariesFilters = Omit<CommentariesQuery, "status"> & {
  status?: Commentary["status"] | Commentary["status"][];
};

export const commentaries = {
  getAll: async (filters?: CommentariesFilters): Promise<CommentariesListResponse> => {
    const params = new URLSearchParams();

    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());

    if (filters?.status) {
      const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
      if (statuses.length > 0) {
        params.append("status", statuses.join(","));
      }
    }

    if (filters?.assignee?.trim()) params.append("assignee", filters.assignee.trim());
    if (filters?.locationId?.trim()) params.append("locationId", filters.locationId.trim());
    if (filters?.bpaId?.trim()) params.append("bpaId", filters.bpaId.trim());
    if (filters?.publicationId?.trim())
      params.append("publicationId", filters.publicationId.trim());
    if (filters?.firmId?.trim()) params.append("firmId", filters.firmId.trim());

    const query = params.toString() ? `?${params.toString()}` : "";
    return api.get<CommentariesListResponse>(`/v1/research/commentaries${query}`);
  },
  getById: async (id: string): Promise<Commentary> => {
    const response = await api.get<CommentaryUpdateSingleResponse>(
      `/v1/research/commentaries/${id}`,
    );
    return response.commentary;
  },
  create: async (data: CreateCommentaryRequest): Promise<Commentary> => {
    const response = await api.post<CommentarySingleResponse>("/v1/research/commentaries", data);
    return response.commentary;
  },
  update: async (id: string, data: UpdateCommentaryRequest): Promise<Commentary> => {
    const response = await api.put<CommentaryUpdateSingleResponse>(
      `/v1/research/commentaries/${id}`,
      data,
    );
    return response.commentary;
  },
  updateStatus: async (id: string, status: Commentary["status"]): Promise<Commentary> => {
    const response = await api.put<{ commentary: Commentary }>(
      `/v1/research/commentaries/${id}/status`,
      { status },
    );
    return response.commentary;
  },
  submitForReview: async (id: string): Promise<Commentary> => {
    const response = await api.post<{ commentary: Commentary }>(
      `/v1/research/commentaries/${id}/submit`,
    );
    return response.commentary;
  },
  approve: async (id: string): Promise<Commentary> => {
    const response = await api.post<{ commentary: Commentary }>(
      `/v1/research/commentaries/${id}/approve`,
    );
    return response.commentary;
  },
  markReadyForProduct: async (id: string): Promise<Commentary> => {
    const response = await api.post<{ commentary: Commentary }>(
      `/v1/research/commentaries/${id}/ready-for-product`,
    );
    return response.commentary;
  },
  returnToDraft: async (id: string): Promise<Commentary> => {
    const response = await api.post<{ commentary: Commentary }>(
      `/v1/research/commentaries/${id}/return-to-draft`,
    );
    return response.commentary;
  },
};
